import React, { useCallback, useEffect, useMemo } from 'react'
import { Stack, Textarea, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import { DateTimePicker } from '@mantine/dates'

import { useModal } from 'src/context/ModalContext'

import { createNoti, editNoti } from 'src/apis/notificationAPI'

import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomModal from 'src/components/CustomComponents/CustomModal'
import CustomSelect from 'src/components/CustomComponents/CustomSelect'

import { NotificationData } from 'src/interfaces'
import { frequencyOpts } from 'src/variable/notificationData'
import { formatDateTimeMinus } from 'src/variable/constant'

import classes from './ModalCreateNotification.module.scss'

interface ModalCreateNotificationProps {
	open: boolean
	data: NotificationData
	onClose: () => void
	handleGetData: () => void
	pagination: {
		pageIndex: number
		pageSize: number
	}
	handleChangePage: (_value: number) => void
	[key: string]: any
}

function ModalCreateNotification({
	open,
	onClose,
	handleGetData,
	pagination,
	handleChangePage,
	data,
}: ModalCreateNotificationProps) {
	const form = useForm({
		initialValues: {
			message_title: '',
			message_content: '',
			frequency: 'ONE_TIME',
			sending_unix_timestamp: dayjs()
				.add(30, 'minutes')
				.startOf('minute')
				.toDate(),
			ending_unix_timestamp: null as Date | null,
		},

		validate: {
			message_title: (value) => {
				if (!value.trim()) return 'Title cannot be empty'
				if (value.length > 50) return 'Title must be 1-50 characters long'
				return null
			},
			message_content: (value) => {
				if (!value.trim()) return 'Content cannot be empty'
				if (value.length > 255) return 'Content must be 1-255 characters long'
				return null
			},
			sending_unix_timestamp: (value) => {
				if (!value) return 'Sending time cannot be empty'
				if (!dayjs(value).isAfter(dayjs(new Date()).add(1, 'minutes')))
					return 'Sending time must be at least 1 minutes greater than current'
				return null
			},
		},
		validateInputOnBlur: true,
		validateInputOnChange: true,
	})
	const { openModal, closeModal, openError } = useModal()
	const text = useMemo(() => {
		const { id } = data || {}
		return id ? 'update' : 'create'
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])
	const handleCreateNotification = useCallback(async ({ body, id = null }) => {
		try {
			const res = await (id ? editNoti(id, body) : createNoti(body))
			if (res?.data?.code === 200) {
				if (id || pagination.pageIndex === 1) {
					await handleGetData()
				} else {
					await handleChangePage(1)
				}
				onClose()
				openModal({
					type: 'success',
					label: `Notification has been ${text} successfully`,
				})
			}
		} catch (error) {
			openError(error)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const handleSubmit = useCallback(async (values) => {
		const { sending_unix_timestamp, ending_unix_timestamp } = values || {}
		const body = {
			...values,
			sending_unix_timestamp: dayjs(sending_unix_timestamp)
				.startOf('minute')
				.valueOf(),
			ending_unix_timestamp: ending_unix_timestamp
				? dayjs(ending_unix_timestamp).startOf('minute').valueOf()
				: null,
		}
		openModal({
			type: 'confirm',
			label: `Do you want to ${text} notification ?`,
			handleClose: () => closeModal(),
			handleConfirm: () => {
				handleCreateNotification({ body, id: data?.id || null })
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	useEffect(() => {
		if (data) {
			const {
				message_title = '',
				message_content = '',
				frequency = 'ONE_TIME',
				sending_unix_timestamp,
				ending_unix_timestamp,
			} = data
			form.setValues({
				message_title,
				message_content,
				frequency,
				sending_unix_timestamp: sending_unix_timestamp
					? dayjs(Number(sending_unix_timestamp)).toDate()
					: null,
				ending_unix_timestamp: ending_unix_timestamp
					? dayjs(Number(ending_unix_timestamp)).toDate()
					: null,
			})
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(data)])
	const handleSendingTimeChange = useCallback(
		(value) => {
			if (value) {
				form.setFieldValue('sending_unix_timestamp', new Date(value))
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)
	const handleEndingTimeChange = useCallback(
		(value) => {
			if (value) {
				form.setFieldValue('ending_unix_timestamp', new Date(value))
				return
			}

			form.setFieldValue('ending_unix_timestamp', null)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	)
	return (
		<CustomModal
			size={720}
			opened={open}
			onClose={onClose}
		>
			<form
				className={classes.modalForm}
				onSubmit={form.onSubmit((values) => handleSubmit(values))}
			>
				<Stack spacing={14}>
					<div className={classes.header}>
						<h2 className={classes.title}>
							{text === 'create'
								? 'Create notification'
								: 'Update notification'}
						</h2>
						<p className={classes.subtitle}>
							Prepare the broadcast copy and schedule window.
						</p>
					</div>
					<section className={classes.section}>
						<div className={classes.sectionHeader}>
							<h3 className={classes.sectionTitle}>Message</h3>
							<p className={classes.sectionCaption}>Required</p>
						</div>
						<div className={classes.fieldGrid}>
							<TextInput
								className={classes.fullWidth}
								autoComplete="off"
								withAsterisk
								key="message_title"
								label="Title"
								name="message_title"
								placeholder="Title"
								maxLength={50}
								{...form.getInputProps('message_title')}
							/>
							<Textarea
								className={classes.fullWidth}
								autoComplete="off"
								withAsterisk
								key="message_content"
								label="Content"
								name="message_content"
								placeholder="Content"
								maxLength={255}
								minRows={4}
								autosize
								{...form.getInputProps('message_content')}
							/>
						</div>
					</section>
					<section className={`${classes.section} ${classes.sectionSoft}`}>
						<div className={classes.sectionHeader}>
							<h3 className={classes.sectionTitle}>Schedule</h3>
							<p className={classes.sectionCaption}>{formatDateTimeMinus}</p>
						</div>
						<div className={`${classes.fieldGrid} ${classes.scheduleGrid}`}>
							<DateTimePicker
								label="Sending time"
								placeholder={formatDateTimeMinus}
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
								minDate={new Date()}
								className={classes.datePicker}
								{...form.getInputProps('sending_unix_timestamp')}
								onChange={handleSendingTimeChange}
							/>
							<DateTimePicker
								className={classes.datePicker}
								label="Ending time"
								placeholder="No ending time"
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
								minDate={new Date()}
								clearable
								{...form.getInputProps('ending_unix_timestamp')}
								onChange={handleEndingTimeChange}
							/>
							<CustomSelect
								label="Frequency"
								data={frequencyOpts}
								sx={{ minWidth: 0, width: '100%' }}
								{...form.getInputProps('frequency')}
							/>
						</div>
						<p className={classes.helper}>
							Time can be scheduled in 1-minute increments.
						</p>
					</section>
					<div className={classes.actions}>
						<CustomButton
							variant="outlined"
							type="submit"
							className={classes.submitButton}
						>
							{text === 'create'
								? 'Create notification'
								: 'Update notification'}
						</CustomButton>
					</div>
				</Stack>
			</form>
		</CustomModal>
	)
}

export default React.memo(ModalCreateNotification)
